import { InputHTMLAttributes } from "react";
import { toast } from "react-toastify";

type LimitedAttributes = Pick<InputHTMLAttributes<HTMLInputElement>, "accept" | "multiple">;

//Validates if a file matches the allowed file types
const validateFile = (file: File, allowedTypes: string[]): boolean => {
  if (!allowedTypes.length) return true;

  // Normalize allowed types
  const normalizedTypes = allowedTypes.map((type) => type.toLowerCase().trim());

  // Check MIME type
  const fileMimeType = file.type.toLowerCase();

  // Check file extension
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf("."));

  for (const allowedType of normalizedTypes) {
    // Check if it's a MIME type (e.g., "image/png", "application/pdf")
    if (allowedType.includes("/")) {
      // Support wildcards like "image/*"
      if (allowedType.endsWith("/*")) {
        const category = allowedType.split("/")[0];
        if (fileMimeType.startsWith(category + "/")) {
          return true;
        }
      } else if (fileMimeType === allowedType) {
        return true;
      }
    }
    // Check if it's a file extension (e.g., ".pdf", ".png")
    else if (allowedType.startsWith(".")) {
      if (fileExtension === allowedType) {
        return true;
      }
    }
  }

  return false;
};

// Filters FileList to only include files that match the allowed types
const filterValidFiles = (fileList: FileList, allowedTypes: string[]): FileList | null => {
  if (allowedTypes.length === 0) return fileList;

  const validFiles: File[] = [];
  const invalidFiles: string[] = [];

  Array.from(fileList).forEach((file) => {
    if (validateFile(file, allowedTypes)) {
      validFiles.push(file);
    } else {
      invalidFiles.push(file.name);
    }
  });

  // Show error message if some files were rejected
  if (invalidFiles.length) {
    const message = "One of the files has an unsupported format or type.";
    toast.error(message);
  }

  // Return null if no valid files, otherwise create a new FileList-like object
  if (validFiles.length === 0) return null;

  // Create a DataTransfer object to generate a proper FileList
  const dataTransfer = new DataTransfer();
  validFiles.forEach((file) => dataTransfer.items.add(file));
  return dataTransfer.files;
};

export default async (attributes?: LimitedAttributes): Promise<FileList | null> => {
  const input = document.createElement("input");
  // Set basic input attributes
  input.type = "file";

  // if external attributes - set them in input
  if (attributes) {
    Object.entries(attributes).forEach(([attrKey, attrValue]) => {
      (input as any)[attrKey] = attrValue; // TS issue to set attributes to input TODO;
    });
  }

  let fileList: FileList | null = null;

  await new Promise((resolve) => {
    input.onchange = (event) => {
      fileList = (event.target as HTMLInputElement).files;
      resolve(true);
    };
    input.oncancel = () => resolve(true);
    input.click();
  });

  // Validate and filter files based on allowed types from accept attribute
  if (fileList && attributes?.accept) {
    // Parse accept attribute (e.g., "image/*,.pdf,.doc" -> ["image/*", ".pdf", ".doc"])
    const allowedTypes = attributes.accept.split(",").map((type) => type.trim());
    fileList = filterValidFiles(fileList, allowedTypes);
  }

  return fileList;
};
